import React from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaLaptopCode, FaMobileAlt, FaPaintBrush, FaSearch, FaChartLine, FaShoppingCart } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
// import ThreeAnimation from '../components/Animation';
import './Home.css';
import Animation from '../components/Animation';

const Home = () => {
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  // Services data
  const services = [
    {
      id: 1,
      icon: <FaLaptopCode />,
      title: 'Custom Website Development',
      description: 'From dynamic landing pages to complex multi-page websites, we create engaging and responsive designs.'
    },
    {
      id: 2,
      icon: <FaMobileAlt />,
      title: 'Mobile Application Development',
      description: 'We build scalable and feature-rich apps that enhance customer experience and drive business growth.'
    },
    {
      id: 3,
      icon: <FaPaintBrush />,
      title: 'UI/UX Design Services',
      description: 'Our design team ensures every platform we build is visually appealing and easy to navigate.'
    },
    {
      id: 4,
      icon: <FaSearch />,
      title: 'SEO Optimization',
      description: 'Boost your online presence with strategies that increase visibility and engagement.'
    },
    {
      id: 5,
      icon: <FaChartLine />,
      title: 'Digital Marketing',
      description: 'Comprehensive digital marketing solutions to help your business reach its target audience.'
    },
    {
      id: 6,
      icon: <FaShoppingCart />,
      title: 'E-commerce Solutions',
      description: 'We create seamless e-commerce platforms to enhance customer journeys and maximize sales.'
    }
  ];

  // Testimonials data
  const testimonials = [
    {
      id: 1,
      name: 'Somith Kumar',
      position: 'CEO, TechCorp',
      content: 'networkBuilder transformed our online presence completely. Their team delivered a website that not only looks great but also performs exceptionally well.',
      image: 'https://randomuser.me/api/portraits/men/4.jpg'
    },
    {
      id: 2,
      name: 'Sarah saie',
      position: 'Marketing Director, Innovate Inc',
      content: 'The mobile application developed by networkBuilder helped us increase customer engagement by 40%. Their attention to detail and user experience is unmatched.',
      image: 'https://randomuser.me/api/portraits/women/8.jpg'
    },
    {
      id: 3,
      name: 'Niranjan S kishan',
      position: 'Founder, StartUp Hub',
      content: 'Working with networkBuilder was a game-changer for our business. Their e-commerce solution increased our online sales by 65% within just one months.',
      image: 'https://randomuser.me/api/portraits/men/26.jpg'
    }
  ];

  // Intersection observer hooks
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [servicesRef, servicesInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [aboutRef, aboutInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [testimonialsRef, testimonialsInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section" ref={heroRef}>
        <div className="overlay"></div>
        <Container>
          <Row className="align-items-center">
            <Col lg={6} md={12}>
              <motion.div 
                className="hero-content"
                initial="hidden"
                animate={heroInView ? "visible" : "hidden"}
                variants={fadeIn}
              >
                <h1>Building Digital Experiences That Transform Businesses</h1>
                <p>We deliver comprehensive web solutions to help businesses establish a strong online presence and achieve their digital goals.</p>
                <div className="hero-buttons">
                  <Link to="/contact" className="btn btn-gradient me-3">Get Started</Link>
                  <Link to="/portfolio" className="btn btn-outline">View Our Work</Link>
                </div>
              </motion.div>
            </Col>
            <Col lg={6} md={12} className="d-flex justify-content-center">
              <div className="hero-animation">
                <Animation height="600px" />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Services Section */}
      <section className="services-section section-padding" ref={servicesRef}>
        <Container>
          <Row className="mb-5">
            <Col lg={12} className="text-center">
              <motion.div
                initial="hidden"
                animate={servicesInView ? "visible" : "hidden"}
                variants={fadeIn}
              >
                <h2 className="section-title mx-auto">Our Services</h2>
                <p className="section-description">We provide a wide range of services to help your business thrive in the digital world.</p>
              </motion.div>
            </Col>
          </Row>
          <motion.div
            initial="hidden"
            animate={servicesInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <Row>
              {services.map((service) => (
                <Col lg={4} md={6} className="mb-4" key={service.id}>
                  <motion.div variants={fadeIn}>
                    <Card className="service-card card-hover h-100">
                      <Card.Body className="text-center p-4">
                        <div className="service-icon">
                          {service.icon}
                        </div>
                        <Card.Title className="service-title my-3">{service.title}</Card.Title>
                        <Card.Text>{service.description}</Card.Text>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* About Section */}
      <section className="about-section section-padding" ref={aboutRef}>
        <Container>
          <Row className="align-items-center">
            <Col lg={6} md={12} className="mb-5 mb-lg-0">
              <motion.div 
                className="about-image"
                initial="hidden"
                animate={aboutInView ? "visible" : "hidden"}
                variants={fadeIn}
              >
                <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80" 
                  alt="Our team working" 
                  className="img-fluid rounded" 
                />
              </motion.div>
            </Col>
            <Col lg={6} md={12}>
              <motion.div 
                className="about-content"
                initial="hidden"
                animate={aboutInView ? "visible" : "hidden"}
                variants={fadeIn}
              >
                <h2 className="section-title">Who We Are</h2>
                <p>At networkBuilder, we're more than just a digital agency. We're a team of passionate technologists, designers, and strategists dedicated to helping businesses thrive in the digital world.</p>
                <p>We combine creativity, technology, and business strategy to deliver solutions that not only look great but also drive tangible results for our clients.</p>
                <ul className="about-list">
                  <li>Experienced team of developers and designers</li>
                  <li>Client-focused approach to every project</li>
                  <li>Commitment to delivering quality and value</li>
                  <li>Innovative solutions using the latest technologies</li>
                </ul>
                <Link to="/about" className="btn btn-gradient mt-3">Learn More About Us</Link>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section section-padding" ref={testimonialsRef}>
        <Container>
          <Row className="mb-5">
            <Col lg={12} className="text-center">
              <motion.div
                initial="hidden"
                animate={testimonialsInView ? "visible" : "hidden"}
                variants={fadeIn}
              >
                <h2 className="section-title mx-auto">What Our Clients Say</h2>
                <p className="section-description">Discover what makes us different through the words of our clients.</p>
              </motion.div>
            </Col>
          </Row>
          <motion.div
            initial="hidden"
            animate={testimonialsInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <Row>
              {testimonials.map((testimonial) => (
                <Col lg={4} md={6} className="mb-4" key={testimonial.id}>
                  <motion.div variants={fadeIn}>
                    <Card className="testimonial-card card-hover h-100">
                      <Card.Body className="p-4">
                        <div className="testimonial-content mb-3">
                          <p className="testimonial-text">"{testimonial.content}"</p>
                        </div>
                        <div className="testimonial-author d-flex align-items-center">
                          <img 
                            src={testimonial.image} 
                            alt={testimonial.name} 
                            className="testimonial-image"
                          />
                          <div className="testimonial-info">
                            <h5 className="mb-0">{testimonial.name}</h5>
                            <small className="text-muted">{testimonial.position}</small>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
          <Row className="mt-4">
            <Col className="text-center">
              <motion.div
                initial="hidden"
                animate={testimonialsInView ? "visible" : "hidden"}
                variants={fadeIn}
              >
                <Link to="/testimonials" className="btn btn-outline">
                  View All Testimonials
                </Link>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10} md={12}>
              <div className="cta-content text-center">
                <h2>Ready to transform your digital presence?</h2>
                <p>Let's work together to create meaningful digital experiences that drive results.</p>
                <Link to="/contact" className="btn btn-gradient">Get in Touch</Link>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default Home; 